import React, { useState } from "react";
import { Create, SaveButton } from "@refinedev/mui";
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Divider,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";
import { IResourceComponentsProps, useApiUrl, useCustomMutation } from "@refinedev/core";
import axios from "axios";

interface CreateBatchFormData {
  count: number;
  description: string;
  validityDays: number;
}

const VALIDITY_PRESETS = [
  { value: 90, label: "3 months (90 days)" },
  { value: 180, label: "6 months (180 days)" },
  { value: 365, label: "1 year (365 days)" },
  { value: 730, label: "2 years (730 days)" },
  { value: 1095, label: "3 years (1095 days)" },
  { value: 1825, label: "5 years (1825 days)" },
];

export const PremiumDeviceCreate: React.FC<IResourceComponentsProps> = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [createdCount, setCreatedCount] = useState(0);
  const [result, setResult] = useState<{
    success: boolean;
    message?: string;
    devices?: any[];
  } | null>(null);

  const apiUrl = useApiUrl();

  const {
    saveButtonProps,
    refineCore: { formLoading },
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    refineCoreProps: {
      redirect: false,
    },
    defaultValues: {
      count: 100,
      description: `Contrat Commercial ${new Date().getFullYear()}`,
      validityDays: 730, // 2 years by default
    },
  });

  const onSubmit = async (data: CreateBatchFormData) => {
    setIsCreating(true);
    setProgress(10);
    setResult(null);

    try {
      const response = await axios.post(`${apiUrl}/premium-devices/batch`, {
        count: data.count,
        description: data.description,
        validityDays: data.validityDays,
      });

      setProgress(100);
      setCreatedCount(response.data.devices?.length || 0);
      setResult({
        success: true,
        message: response.data.message,
        devices: response.data.devices,
      });
    } catch (error: any) {
      setResult({
        success: false,
        message: error.response?.data?.message || error.message || "Failed to create batch",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Create
      isLoading={formLoading}
      saveButtonProps={{
        ...saveButtonProps,
        onClick: handleSubmit(onSubmit),
        disabled: isCreating,
      }}
      title="Create Premium Device Batch"
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        autoComplete="off"
      >
        <Alert severity="info">
          <Typography variant="body2">
            Create multiple premium devices at once with the same configuration.
            Each device will have a unique MAC address and serial number.
          </Typography>
        </Alert>

        <TextField
          {...register("count", {
            required: "This field is required",
            min: { value: 1, message: "Minimum 1 device" },
            max: { value: 1000, message: "Maximum 1000 devices per batch" },
          })}
          error={!!errors.count}
          helperText={errors.count?.message as string}
          margin="normal"
          fullWidth
          label="Number of Devices"
          type="number"
          InputProps={{
            inputProps: { min: 1, max: 1000 },
          }}
        />

        <TextField
          {...register("description", {
            required: "This field is required",
            minLength: { value: 3, message: "Minimum 3 characters" },
          })}
          error={!!errors.description}
          helperText={errors.description?.message as string}
          margin="normal"
          fullWidth
          label="Batch Description"
          placeholder="e.g., Commercial Contract Q1 2025"
        />

        <Controller
          control={control}
          name="validityDays"
          rules={{ required: "This field is required" }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              error={!!errors.validityDays}
              helperText={errors.validityDays?.message as string}
              margin="normal"
              fullWidth
              label="Validity Duration"
            >
              {VALIDITY_PRESETS.map((preset) => (
                <MenuItem key={preset.value} value={preset.value}>
                  {preset.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Divider />

        {isCreating && (
          <Box sx={{ width: "100%", mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Creating devices...
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {result && (
          <Alert severity={result.success ? "success" : "error"} sx={{ mt: 2 }}>
            <Typography variant="body2">
              {result.success
                ? `Successfully created ${createdCount} devices!`
                : `Error: ${result.message}`}
            </Typography>
          </Alert>
        )}

        {result?.success && result.devices && (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Batch Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Devices: {result.devices.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Description: {result.devices[0]?.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Validity: {result.devices[0]?.validityDays} days
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  First device MAC: {result.devices[0]?.macAddress}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  Last device MAC: {result.devices[result.devices.length - 1]?.macAddress}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Create>
  );
};
